import {
  amendHomeworkInputSchema,
  cancelHomeworkInputSchema,
  reopenHomeworkInputSchema,
  requestHomeworkReminderInputSchema,
} from '@cc/schema';
import { useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import { parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { draftOfSchedule } from '../../components/EsCardReminders/rrule';
import { DateFnsDatePicker } from '../../components/Pickers';
import { SubmitFooter } from '../../components/SubmitFooter';
import {
  REMINDER_POLICIES,
  actLockOf,
  browserZone,
  defaultDueTime,
  type HomeworkRow,
} from './model';
import {
  RepeatEditor,
  freshRepeat,
  repeatOfSchedule,
  repeatProblem,
  scheduleOfRepeat,
  type RepeatDraft,
} from './RepeatEditor';
import { useHomeworkCommand } from './useHomeworkCommand';
import { usePickerLocale } from './usePickerLocale';

/**
 * E34 · the hub row's four practice acts, each its own section on the board (cc-homework, chapter
 * "Homework — Assign & follow (portal, E34)"):
 *   · Edit (AmendHomework) — title, instructions, due instant, reminder policy; "kind and target
 *     never change — a different task is a new assignment"; only the fields changed are sent; a
 *     recurring row edits its SCHEDULE with the Assign drawer's rule editor instead of a due instant
 *     (the founder's Q-b1a-5 B — the zone is the one the row already runs in, and the server fills
 *     the therapist's own for "earlier today");
 *     a tool's "May do it on paper" switch (Q-paper-3 B) is sent only when changed — and only a tool has one;
 *   · a past due instant is accepted only earlier the same day (the founder's Q-b1b-5 C — the server
 *     decides in the therapist's calendar day): the pickers do not grey out the days before today,
 *     the server's refusal (HOMEWORK_DUE_IN_PAST) is shown in the dialog;
 *   · Cancel (CancelHomework) — "confirm with reason"; the reason is kept;
 *   · Reopen (ReopenHomework) — a done, skipped or cancelled row back to open, with an optional reason
 *     and new due instant;
 *   · Send a reminder (RequestHomeworkReminder) — an optional line; at most once a day (the caller
 *     greys the button; the server refuses a second one — HOMEWORK_REMINDED_TODAY).
 * Each is ONE deliberate act (a confirm button); a refusal is shown verbatim inside the dialog.
 */
export type HomeworkAct = 'edit' | 'cancel' | 'reopen' | 'remind';

type Props = {
  act: HomeworkAct | null;
  row: HomeworkRow | null;
  /** Known when the dialog opens (Q-mt-2 A): the client is no longer connected to the practice. */
  notConnected?: boolean;
  onClose: () => void;
  onDone: () => void;
};

export const HomeworkActModal = ({
  act,
  row,
  notConnected,
  onClose,
  onDone,
}: Props) => {
  const t = useTranslate();
  const pickerLocale = usePickerLocale();
  const { message } = AntdApp.useApp();
  const { run } = useHomeworkCommand();

  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [dueAt, setDueAt] = useState<Date | null>(null);
  const [policy, setPolicy] = useState('default');
  // Q-paper-3 B: a tool's paper switch — sent only when the therapist changed it
  const [paperAllowed, setPaperAllowed] = useState(false);
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [repeat, setRepeat] = useState<RepeatDraft>(freshRepeat);
  // the schedule is sent only when the therapist touched the editor (a rule the portal cannot build —
  // a bound the phone set — is never rewritten by an Edit of the title)
  const [repeatTouched, setRepeatTouched] = useState(false);

  useEffect(() => {
    if (!act || !row) return;
    setTitle(row.title);
    setInstructions(row.instructions ?? '');
    setDueAt(act === 'edit' && row.dueAt ? parseISO(row.dueAt) : null);
    setPolicy(row.reminderPolicy || 'default');
    setPaperAllowed(!!row.paperAllowed);
    setReason('');
    setText('');
    setProblem(null);
    setRepeat(
      row.schedule
        ? repeatOfSchedule(
            draftOfSchedule({
              rrule: row.schedule.rrule,
              dtstart: row.schedule.dtstart,
            }),
          )
        : freshRepeat(),
    );
    setRepeatTouched(false);
  }, [act, row]);

  // Q-mt-2 A: a submit error clears on the next edit
  useEffect(() => {
    setProblem(null);
  }, [title, instructions, dueAt, policy, paperAllowed, reason, text, repeat]);

  if (!act || !row) return null;
  const recurring = !!row.schedule;
  // known when the dialog opens: stays at the TOP, and the confirm button is disabled while it holds
  const lock = actLockOf(act, row, notConnected);

  const finish = async (
    input: Record<string, unknown>,
    op:
      | 'amendHomework'
      | 'cancelHomework'
      | 'reopenHomework'
      | 'requestHomeworkReminder',
    doneKey: string,
    failedKey: string,
  ) => {
    setSaving(true);
    const r = await run(op, input, t(failedKey));
    setSaving(false);
    if (!r.ok) {
      setProblem(r.message);
      return;
    }
    message.success(t(doneKey));
    onDone();
    onClose();
  };

  const submit = async () => {
    setProblem(null);
    const assignmentId = row.assignmentId;
    try {
      if (act === 'edit') {
        const changes: Record<string, unknown> = {};
        if (title.trim() !== row.title) {
          if (!title.trim()) {
            setProblem(t('homework.assign.required.title'));
            return;
          }
          changes.title = title.trim();
        }
        if ((instructions.trim() || null) !== (row.instructions ?? null))
          changes.instructions = instructions.trim() || null;
        if (!recurring) {
          const before = row.dueAt ? parseISO(row.dueAt).getTime() : null;
          const after = dueAt ? dueAt.getTime() : null;
          if (before !== after)
            changes.dueAt = dueAt ? dueAt.toISOString() : null;
        } else if (repeatTouched && row.schedule) {
          if (repeatProblem(repeat) === 'WEEKDAY_REQUIRED') {
            setProblem(t('homework.assign.required.weekdays'));
            return;
          }
          const next = scheduleOfRepeat(repeat, row.schedule.tzid || browserZone());
          if (
            next.rrule !== row.schedule.rrule ||
            next.dtstart !== row.schedule.dtstart
          )
            changes.schedule = next;
        }
        if (policy !== row.reminderPolicy) changes.reminderPolicy = policy;
        // Q-paper-3 B: only when touched (null = unchanged on the wire) — and only a tool has the switch
        if (row.kind === 'tool' && paperAllowed !== !!row.paperAllowed)
          changes.paperAllowed = paperAllowed;
        if (Object.keys(changes).length === 0) {
          message.info(t('homework.edit.nothing'));
          onClose();
          return;
        }
        const input = { assignmentId, ...changes };
        await amendHomeworkInputSchema.validate(input);
        await finish(
          input,
          'amendHomework',
          'homework.edit.done',
          'homework.edit.failed',
        );
      } else if (act === 'cancel') {
        if (!reason.trim()) {
          setProblem(t('homework.cancel.reasonRequired'));
          return;
        }
        const input = { assignmentId, reason: reason.trim() };
        await cancelHomeworkInputSchema.validate(input);
        await finish(
          input,
          'cancelHomework',
          'homework.cancel.done',
          'homework.cancel.failed',
        );
      } else if (act === 'reopen') {
        const input = {
          assignmentId,
          reason: reason.trim() || null,
          dueAt: !recurring && dueAt ? dueAt.toISOString() : null,
        };
        await reopenHomeworkInputSchema.validate(input);
        await finish(
          input,
          'reopenHomework',
          'homework.reopen.done',
          'homework.reopen.failed',
        );
      } else {
        const input = { assignmentId, message: text.trim() || null };
        await requestHomeworkReminderInputSchema.validate(input);
        await finish(
          input,
          'requestHomeworkReminder',
          'homework.remind.done',
          'homework.remind.failed',
        );
      }
    } catch {
      setProblem(t(`homework.${act}.failed`));
    }
  };

  const heading = `${t(`homework.${act}.title`)} — “${row.title}”`;
  const confirmKey =
    act === 'edit'
      ? 'homework.edit.save'
      : act === 'cancel'
      ? 'homework.cancel.confirm'
      : act === 'reopen'
      ? 'homework.reopen.confirm'
      : 'homework.remind.confirm';

  return (
    <Modal
      open
      title={heading}
      onCancel={onClose}
      destroyOnClose
      footer={
        <SubmitFooter
          errors={
            problem ? (
              <Alert
                type="error"
                showIcon
                message={problem}
                data-testid="homework-act-problem"
              />
            ) : null
          }
        >
          <Button onClick={onClose} data-testid="homework-act-back">
            {act === 'cancel'
              ? t('homework.cancel.keep')
              : t('homework.assign.back')}
          </Button>
          <Button
            type="primary"
            danger={act === 'cancel'}
            loading={saving}
            disabled={!!lock}
            onClick={submit}
            data-testid={`homework-act-confirm-${act}`}
          >
            {t(confirmKey)}
          </Button>
        </SubmitFooter>
      }
    >
      <Space
        direction="vertical"
        size="middle"
        style={{ width: '100%' }}
        data-testid={`homework-act-${act}`}
      >
        {lock ? (
          <Alert
            type="error"
            showIcon
            message={t(`homework.${act}.rowLocked`, {
              reason: t(`homework.lock.${lock}`),
            })}
            data-testid={`homework-${act}-locked`}
          />
        ) : null}

        {act !== 'edit' ? (
          <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
            {t(`homework.${act}.notice`)}
          </Typography.Paragraph>
        ) : (
          <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
            {t('homework.edit.notice')}
          </Typography.Paragraph>
        )}

        {act === 'edit' ? (
          <>
            <div>
              <Typography.Text>
                {t('homework.assign.titleLabel')}
              </Typography.Text>
              <Input
                value={title}
                maxLength={200}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="homework-edit-title"
              />
            </div>
            <div>
              <Typography.Text>
                {t('homework.assign.instructions')}
              </Typography.Text>
              <Input.TextArea
                value={instructions}
                maxLength={4000}
                autoSize={{ minRows: 2, maxRows: 6 }}
                onChange={(e) => setInstructions(e.target.value)}
                data-testid="homework-edit-instructions"
              />
            </div>
            {recurring && row.schedule ? (
              <div data-testid="homework-edit-schedule">
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message={t('homework.edit.recurringNote')}
                />
                <RepeatEditor
                  value={repeat}
                  onChange={(next) => {
                    setRepeat(next);
                    setRepeatTouched(true);
                  }}
                  testPrefix="homework-edit"
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('homework.assign.clientZone', { zone: row.schedule.tzid || browserZone() })}
                </Typography.Text>
              </div>
            ) : (
              <div>
                <Typography.Text>{t('homework.assign.dueAt')}</Typography.Text>
                <DateFnsDatePicker
                  locale={pickerLocale}
                  style={{ width: '100%' }}
                  showTime={{
                    format: 'HH:mm',
                    minuteStep: 5,
                    defaultValue: defaultDueTime(),
                  }}
                  format="yyyy-MM-dd HH:mm"
                  value={dueAt}
                  onChange={(d: Date | null) => setDueAt(d)}
                  data-testid="homework-edit-due"
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('homework.assign.yourZone', { zone: browserZone() })}
                </Typography.Text>
              </div>
            )}
            <div>
              <Typography.Text>
                {t('homework.assign.reminderPolicy')}
              </Typography.Text>
              <Select
                style={{ width: '100%' }}
                value={policy}
                onChange={setPolicy}
                options={REMINDER_POLICIES.map((p) => ({
                  value: p,
                  label: t(`homework.reminderPolicy.${p}`),
                }))}
                data-testid="homework-edit-reminder-policy"
              />
            </div>
            {row.kind === 'tool' ? (
              <div>
                <Space size={8} align="start">
                  <Switch
                    size="small"
                    checked={paperAllowed}
                    onChange={setPaperAllowed}
                    data-testid="homework-edit-paper-allowed"
                  />
                  <Typography.Text>{t('homework.assign.paperAllowed')}</Typography.Text>
                </Space>
                <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: '2px 0 0' }}>
                  {t('homework.assign.paperAllowedHelp')}
                </Typography.Paragraph>
              </div>
            ) : null}
            {row.kind === 'worksheet' ? (
              // Q-paper-2 A: a worksheet's paper is the template's (automatic) — read only here
              <Typography.Paragraph
                type="secondary"
                style={{ fontSize: 12, margin: 0 }}
                data-testid="homework-edit-paper-worksheet"
              >
                {t(
                  row.paperAllowed
                    ? 'homework.assign.paperWorksheet.yes'
                    : 'homework.assign.paperWorksheet.no',
                )}
              </Typography.Paragraph>
            ) : null}
          </>
        ) : null}

        {act === 'cancel' || act === 'reopen' ? (
          <div>
            <Typography.Text>{t(`homework.${act}.reason`)}</Typography.Text>
            <Input.TextArea
              value={reason}
              maxLength={1000}
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder={t(`homework.${act}.reasonPlaceholder`)}
              onChange={(e) => setReason(e.target.value)}
              data-testid={`homework-${act}-reason`}
            />
          </div>
        ) : null}

        {act === 'reopen' && !recurring ? (
          <div>
            <Typography.Text>{t('homework.reopen.dueAt')}</Typography.Text>
            <DateFnsDatePicker
              locale={pickerLocale}
              style={{ width: '100%' }}
              showTime={{ format: 'HH:mm', minuteStep: 5 }}
              format="yyyy-MM-dd HH:mm"
              value={dueAt}
              onChange={(d: Date | null) => setDueAt(d)}
              data-testid="homework-reopen-due"
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('homework.assign.yourZone', { zone: browserZone() })}
            </Typography.Text>
          </div>
        ) : null}

        {act === 'remind' ? (
          <div>
            <Typography.Text>{t('homework.remind.message')}</Typography.Text>
            <Input.TextArea
              value={text}
              maxLength={500}
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder={t('homework.remind.messagePlaceholder')}
              onChange={(e) => setText(e.target.value)}
              data-testid="homework-remind-message"
            />
          </div>
        ) : null}

      </Space>
    </Modal>
  );
};
